import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AccountSettings from './AccountSettings';
import DailyPocketLogo from './DailyPocketLogo';
import './Header.css';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
  };

  const toggleSettingsDropdown = () => {
    setShowSettingsDropdown(!showSettingsDropdown);
  };

  const closeSettingsDropdown = () => {
    setShowSettingsDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeSettingsDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="app-title">
          <DailyPocketLogo width={36} height={36} className="app-logo" />
          <h1>{t('common:appName')}</h1>
        </div>
        <div className="header-actions">
          <div className="user-info">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="user-avatar"
              />
            )}
            <span className="user-name">{user?.name}</span>
            
            {/* Settings Dropdown */}
            <div className="settings-dropdown" ref={dropdownRef}>
              <button 
                className="settings-btn"
                onClick={toggleSettingsDropdown}
                aria-label={t('account:accountSettings')}
                title={t('account:accountSettings')}
              >
                ⚙️
              </button>
              
              {showSettingsDropdown && (
                <div className="settings-dropdown-content">
                  <div className="settings-dropdown-header">
                    <h3>{t('account:accountSettings')}</h3>
                    <button 
                      className="close-settings-btn"
                      onClick={closeSettingsDropdown}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="settings-dropdown-body">
                    <AccountSettings onClose={closeSettingsDropdown} />
                  </div>
                </div>
              )}
            </div>
            
            <button 
              className="logout-btn"
              onClick={handleLogout}
              title={t('auth:logout')}
              aria-label={t('auth:logout')}
            >
              <span className="logout-icon">🚪</span>
              <span className="logout-text">{t('auth:logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;